import React, { useEffect, useState } from "react";
import CgFormCreator from "../../common/CgFormCreator";
import "./AddProviders.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  addProvidersDetailsAPI,
  fetchAddProvidersCertifiedSpecialty,
  fetchAddProvidersLicenseTypes,
} from "../../store/Admin/AddProvidersActions";
import { useNavigate } from "react-router-dom";
import statesData from "../../utils/commonUtilities";

const suffixDropdownOptions = [
  { id: 0, value: "None" },
  { id: 1, value: "Jr" },
  { id: 2, value: "Sr" },
  { id: 3, value: "II" },
  { id: 4, value: "III" },
  { id: 5, value: "IV" },
  { id: 6, value: "V" },
  { id: 7, value: "VI" },
  { id: 8, value: "VII" },
  { id: 9, value: "VIII" },
  { id: 10, value: "IX" },
];

const genderRadioOptions = [
  {
    code: "male",
    name: "Male",
  },
  {
    code: "female",
    name: "Female",
  },
];

const AddProviders = () => {
  const [validatedValues, setValidatedValues] = useState({
    firstName: "",
    lastName: "",
    inPracticeSinceDate: "",
    licenseNumber: "",
    licenseState: "",
    licenseType: "",
    dateOfBirth: "",
    issueDate: "",
    nationalProviderIdentifier: "",
    npiUnavailable: false,
    issueDateUnavailable: false,
    gender: "",
    specialtyCodes: [],
  });
  const [licenseTypeDropdownOptions, setLicenseTypeDropdownOptions] = useState(
    []
  );
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);

  const { addProvidersLicenseTypeErrorMsg } = useSelector(
    (state) => state?.addProvidersLicenseTypes
  );
  const { addProvidersCertifiedSpecialty, certifiedSpecialtyErrorMsg } =
    useSelector((state) => state?.addProvidersCertifiedSpecialty);
  const { addProvidersErrorMsg } = useSelector((state) => state?.addProviders);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchAddProvidersCertifiedSpecialty());
  }, []);

  useEffect(() => {
    if (validatedValues?.licenseState?.length > 0) {
      dispatch(
        fetchAddProvidersLicenseTypes(validatedValues?.licenseState)
      ).then((response) => {
        const licenseTypes =
          response?.payload?.data?.data?.licenseTypeResponseDTO?.length > 0 &&
          response?.payload?.data?.data?.licenseTypeResponseDTO?.map(
            (item, index) => ({
              id: index,
              value: item?.name,
            })
          );
        setLicenseTypeDropdownOptions(licenseTypes || []);
      });
    }
    if (validatedValues?.npiUnavailable === true) {
      setValidatedValues((prevValues) => ({
        ...prevValues,
        nationalProviderIdentifier: "",
      }));
    }
  }, [validatedValues?.licenseState, validatedValues?.npiUnavailable]);

  useEffect(() => {
    if (
      addProvidersLicenseTypeErrorMsg ||
      certifiedSpecialtyErrorMsg ||
      addProvidersErrorMsg
    ) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 2000);
    }
  }, [
    addProvidersLicenseTypeErrorMsg,
    certifiedSpecialtyErrorMsg,
    addProvidersErrorMsg,
  ]);

  const handleDisablePrimaryButton = () => {
    if (
      validatedValues?.firstName?.length === 0 ||
      validatedValues?.lastName?.length === 0 ||
      validatedValues?.licenseState?.length === 0 ||
      validatedValues?.licenseType?.length === 0 ||
      validatedValues?.licenseNumber?.length === 0 ||
      (!validatedValues?.npiUnavailable &&
        validatedValues?.nationalProviderIdentifier?.length > 0 &&
        validatedValues?.nationalProviderIdentifier?.length !== 10)
    ) {
      return false;
    }
    return true;
  };

  const getFormValues = (data) => {
    const formValues = {};
    for (const key in data) {
      if (data[key] !== "") {
        formValues[key] = data[key];
      }
    }
    return formValues;
  };

  const validateDates = (values) => {
    const currentYear = new Date().getFullYear();
    const errors = {};

    if (values.inPracticeSinceDate) {
      const practiceYear = new Date(values.inPracticeSinceDate).getFullYear();
      if (practiceYear > currentYear) {
        errors.inPracticeSinceDateError =
          "Practicing since date cannot be in the future";
      }
    }
    if (values.issueDate && !values.issueDateUnavailable) {
      const issueYear = new Date(values.issueDate).getFullYear();
      if (issueYear > currentYear) {
        errors.issueDateError = "Issue date cannot be in the future";
      }
    }
    return errors;
  };

  const modifiedAddProvidersCertifiedSpecialty = () => {
    return addProvidersCertifiedSpecialty.map((item) => ({
      code: item.code,
      name: `${item.name} (${item.code})`,
    }));
  };

  const handleAddProvidersData = (addProvidersData) => {
    const formData = getFormValues(addProvidersData);
    if (formData?.issueDateUnavailable?.length === 0) {
      delete formData?.issueDateUnavailable;
    }
    formData.npiUnavailable = validatedValues?.npiUnavailable;
    formData.issueDateUnavailable = validatedValues?.issueDateUnavailable;
    if (formData?.issueDate?.length > 0) {
      const dateError = validateDates(formData);
      if (dateError?.issueDateError?.length > 0) {
        setErrorMsg(dateError?.issueDateError);
        return;
      }
      setErrorMsg("");
    }
    if (formData?.inPracticeSinceDate?.length > 0) {
      const dateError = validateDates(formData);
      if (dateError?.inPracticeSinceDateError?.length > 0) {
        setErrorMsg(dateError?.inPracticeSinceDateError);
        return;
      }
      setErrorMsg("");
    }
    if (formData["radio-group"]?.length > 0) {
      formData.gender = formData["radio-group"];
      delete formData["radio-group"];
    }
    setErrorMsg("");
    dispatch(addProvidersDetailsAPI(formData)).then((response) => {
      if (
        response?.payload?.success &&
        response?.payload?.data?.data?.providerDto
      ) {
        const displayId = response.payload.data.data.providerDto.displayId;
        navigate(`/provider/${displayId}/profile-information/info`);
      }
    });
  };

  const addProviderSchema = [
    {
      component: "input",
      label: "FIRST NAME",
      type: "text",
      name: "firstName",
      required: true,
      placeholder: " ",
      className:
        validatedValues?.firstName?.length === 0
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      label: "MIDDLE NAME",
      type: "text",
      name: "middleName",
      placeholder: " ",
      className: "addProvidersFields",
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      label: "LAST NAME",
      type: "text",
      name: "lastName",
      required: true,
      placeholder: " ",
      className:
        validatedValues?.lastName?.length === 0
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      label: "PREFERRED FIRST NAME",
      type: "text",
      name: "nickName",
      placeholder: " ",
      className: "addProvidersFields",
      valueChangeCallback: () => {},
    },
    {
      component: "dropdown",
      label: "SUFFIX",
      name: "suffix",
      dropdownOptions: suffixDropdownOptions,
      onSelectCallBack: () => {},
      className: "addProvidersFields",
    },
    {
      component: "input",
      label: "DATE OF BIRTH",
      type: "date",
      name: "dateOfBirth",
      className:
        validatedValues?.dateOfBirth?.length > 0 &&
        validatedValues?.dateOfBirth?.length !== 10
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
    },
    {
      component: "radio",
      name: "gender",
      className: "addProvidersGender",
      options: genderRadioOptions,
      keyValues: { label: "name", value: "code" },
      onSelect: (name, value) => {
        setValidatedValues({ ...validatedValues, gender: value });
      },
      customRenderLabel: () => {
        return <div>Gender</div>;
      },
    },
    {
      component: "input",
      label: "NPI",
      type: "text",
      name: "nationalProviderIdentifier",
      placeholder: " ",
      defaultValue: validatedValues?.nationalProviderIdentifier,
      resetValue: validatedValues?.npiUnavailable,
      disabled: validatedValues?.npiUnavailable,
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
      className:
        validatedValues?.nationalProviderIdentifier?.length &&
        validatedValues?.nationalProviderIdentifier?.length !== 10 &&
        !validatedValues?.npiUnavailable
          ? "npiErrorBorder"
          : "addProvidersFields",
      showCheckbox: true,
      checkboxLabel: "NPI not available",
      checkboxName: "npiUnavailable",
      checkboxDefaultValue: validatedValues?.npiUnavailable,
      onCheckboxValueChange: () => {
        setValidatedValues({
          ...validatedValues,
          npiUnavailable: !validatedValues.npiUnavailable,
        });
      },
      shouldRenderInputCheckboxWithBackgroundImageStyle: true,
    },
    {
      component: "input",
      label: "PRACTICE SINCE",
      type: "date",
      name: "inPracticeSinceDate",
      className: "addProvidersFields",
      valueChangeCallback: () => {},
    },
    {
      component: "custom",
      className: "formHeadingTitle",
      customRender: () => {
        return <div className="formCreatorHeader">PRIMARY LICENSE</div>;
      },
    },
    {
      component: "dropdown",
      label: "LICENSE STATE",
      name: "licenseState",
      dropdownOptions: statesData,
      dropdownPlaceHolder: "Select...",
      className:
        validatedValues?.licenseState?.length === 0
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      onSelectCallBack: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      required: true,
    },
    {
      component: "dropdown",
      label: "LICENSE TYPE",
      name: "licenseType",
      dropdownPlaceHolder: "Select...",
      dropdownOptions: licenseTypeDropdownOptions,
      className:
        validatedValues?.licenseType?.length === 0
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      disabled:
        validatedValues?.licenseState?.length === 0 ||
        licenseTypeDropdownOptions?.length === 0,
      onSelectCallBack: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      required: true,
    },
    {
      component: "input",
      label: "LICENSE NUMBER",
      type: "text",
      name: "licenseNumber",
      placeholder: " ",
      className:
        validatedValues?.licenseNumber?.length === 0
          ? "addProvidersErrorBorder"
          : "addProvidersFields",
      onKeyChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      valueChangeCallback: () => {},
      required: true,
    },
    {
      component: "input",
      label: "ISSUE DATE",
      type: "date",
      name: "issueDate",
      placeholder: " ",
      defaultValue: validatedValues?.issueDate,
      resetValue: validatedValues?.issueDateUnavailable,
      valueChangeCallback: (name, value) => {
        setValidatedValues({ ...validatedValues, [name]: value });
      },
      disabled: validatedValues?.issueDateUnavailable,
      showCheckbox: true,
      checkboxLabel: "Valid issue date not found",
      checkboxName: "issueDateUnavailable",
      checkboxDefaultValue: validatedValues?.issueDateUnavailable,
      onCheckboxValueChange: () => {
        setValidatedValues({
          ...validatedValues,
          issueDateUnavailable: !validatedValues?.issueDateUnavailable,
        });
      },
      shouldRenderInputCheckboxWithBackgroundImageStyle: true,
    },
    {
      component: "custom",
      className: "formHeadingTitle",
      customRender: () => {
        return <div className="formCreatorHeader">CERTIFIED SPECIALTY</div>;
      },
    },
    {
      component: "checkbox",
      shouldRenderCheckboxWithBackgroundImageStyle: true,
      name: "specialtyCodes",
      defaultValues: validatedValues?.specialtyCodes,
      options: modifiedAddProvidersCertifiedSpecialty(),
      className: "specialtyCodes",
      keyValues: { label: "name", value: "code" },
      onSelect: () => {},
      customRenderLabel: () => {},
    },
  ];

  const RenderAddProvidersError = () => {
    if (!showError) return null;
    return (
      <div
        className="overlay-alert alert alert-danger alertTopPosition"
        role="alert"
      >
        {addProvidersErrorMsg ||
          addProvidersLicenseTypeErrorMsg ||
          certifiedSpecialtyErrorMsg}
      </div>
    );
  };

  const RenderAddProvidersForm = () => {
    return (
      <div className="addProviderWrapper">
        <h1>Add New Provider</h1>
        <CgFormCreator
          key={validatedValues}
          onSubmitCallback={(res) => {
            handleAddProvidersData(res);
          }}
          onCancel={() => navigate("/practice/profileinformation/dashboard")}
          enablePrimaryBtn={handleDisablePrimaryButton()}
          primaryButton="ADD"
          needFormHeader={true}
          resetValuesOnSubmit={errorMsg && false}
          formHeader="PROVIDER INFORMATION"
          formClassName="addProviderFormWrapper"
          schema={addProviderSchema}
          showErrorMsg={errorMsg}
        />
      </div>
    );
  };

  return (
    <>
      {RenderAddProvidersForm()}
      {RenderAddProvidersError()}
    </>
  );
};

export default AddProviders;
